import type { Language } from '@/config/lang'
import { getLanguage } from '@/utils/language'
const MINUTES_IN_HOUR = 60
const HOURS_IN_DAY = 24

/** Бэкенд отдаёт время работы минутами от полуночи: 630 → «10:30». */
export function minutesToClock(value: number): string {
  const total = Math.max(0, Math.round(value))
  const hours = Math.floor(total / MINUTES_IN_HOUR) % HOURS_IN_DAY
  const minutes = total % MINUTES_IN_HOUR
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/** «10:30 - 23:00» — так время филиала показано в макете. */
export function formatWorkTime(start: number, end: number): string {
  return `${minutesToClock(start)} - ${minutesToClock(end)}`
}

/**
 * Дата по-русски: «12 августа», с годом — «12 августа 1994».
 * Хвост « г.» от `toLocaleDateString` убираем: в макете его нет.
 */
/** BCP-47 для `toLocaleDateString`: у узбекского латиница, иначе месяцы кириллицей. */
const DATE_LOCALES: Record<Language, string> = { ru: 'ru-RU', uz: 'uz-Latn-UZ', en: 'en-US' }

export function formatDate(value: string, withYear = false): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  /* Локаль берём текущую: месяц должен читаться на языке интерфейса */
  return date
    .toLocaleDateString(DATE_LOCALES[getLanguage()], {
      day: 'numeric',
      month: 'long',
      ...(withYear ? { year: 'numeric' } : {}),
    })
    .replace(' г.', '')
}

/** «25.07.2026» — числовая дата в карточке уведомления. */
export function formatDateNumeric(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${date.getFullYear()}`
}

/** «13:57» — время публикации из ISO-строки. */
export function formatClock(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Дата рождения из строки бэкенда в `Date` для календаря.
 * Приходит и `1994-08-12`, и `1994-08-12T00:00:00` — оба варианта читаются.
 */
export function parseDate(value: string): Date | null {
  if (!value) return null

  /* Голую дату собираем по частям: `new Date('1994-08-12')` — это UTC-полночь,
     и в отрицательных поясах календарь подсветил бы 11 августа */
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Обратно в `YYYY-MM-DD` — тот же формат, что store кладёт в payload.
 * Считаем по локальным полям: `toISOString()` уводит дату на день назад
 * для всех, кто восточнее Гринвича, а Ташкент это +5.
 */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}
